/* ============================================
   ROOM DESIGNER — Curved Walls (grouped facets)
   Asset file: custom-room-designer-curve.js
   CUSTOM_ROOMDESIGNER_BY_CLAUDE

   A "curved wall" is N ordinary straight wall objects (facets) that share a
   `curveId` and carry an identical, authoritative `curveArc`. To the user it
   behaves as ONE wall (one selection, one dimension label, one drag handle).
   To the rest of the engine — wallseg.js, door.js, zone detection, area
   calculation, flooring take-off — the facets are just plain straight walls,
   so none of those modules need to know curves exist.

   FACET WALL SHAPE (all fields additive; absent = ordinary straight wall)
     { start, end,
       curveId:  'c_...'                       shared by every facet of a curve
       curveSeq: 0..n-1                        position within the curve
       curveArc: { cx, cy, r, a0, a1, ccw, n, sag } }

   WHY THE FACET COUNT IS FIXED
     `door.wall` is a direct object reference (door.js:159 et al). If reshaping a
     curve REPLACED its facet objects, every door on that curve would point at a
     wall no longer in room.walls. So `n` is fixed at creation and reshaping
     mutates the existing facet objects in place — doors keep their reference and
     their param `t`, and simply ride the new geometry.

   ANGLE CONVENTION
     Matches RoomDesignerMath._angleInArc: `ccw === false` means the angle
     INCREASES from a0 to a1; `ccw === true` means it DECREASES.

   Pure geometry, no instance state. Loaded before custom-room-designer.js.

   SECTION MAP — update line numbers when major sections shift
   ───────────────────────────────────────────
   L57-71      Small predicates (isCurved, sameCurve, nextCurveId)
   L73-157     Arc fitting (_chordFrame, fitArc, sweepOf, arcLength, sagittaFrom,
               midHandlePoint)
   L159-188    Tessellation (tessellate)
   L190+       Facet lifecycle (facetsOf, buildFacets, applyToFacets, refitToChord,
               collapseToStraight, endpointsOf, isInteriorJoint)
   ============================================ */

window.RoomDesignerCurve = (() => {
  const C = {};
  const TWO_PI = Math.PI * 2;

  /** Below this sagitta (feet) a curve is indistinguishable from a straight wall. */
  C.FLAT_EPS = 0.01;          // ~1/8 inch — the floor on a usable bow
  /**
   * Default facet count per curve. Fixed for the lifetime of a curve (see the
   * header note on door references), so it has to be generous enough for the
   * worst sweep the user can drag to. Measured chord-vs-arc error at n=24:
   * 90° sweep 0.017%, 180° 0.071%, 232° (major) 0.118% — sub-millimetre on a
   * 20ft wall even at the extreme.
   */
  C.DEFAULT_FACETS = 24;
  /** Minimum wall left beside an opening, as a fraction of the arc. */
  C.MIN_JAMB = 0.01;

  const norm = (a) => ((a % TWO_PI) + TWO_PI) % TWO_PI;

  // ── Small predicates ──────────────────────

  C.isCurved = function(wall) {
    return !!(wall && wall.curveId);
  };

  C.sameCurve = function(w1, w2) {
    return !!(w1 && w2 && w1.curveId && w1.curveId === w2.curveId);
  };

  let _curveSeed = 0;
  C.nextCurveId = function() {
    _curveSeed++;
    return 'c_' + Date.now().toString(36) + '_' + _curveSeed.toString(36);
  };

  // ── Arc fitting ───────────────────────────

  /**
   * Unit chord vector and its left normal for a start→end chord.
   * Returns null for a degenerate (zero-length) chord.
   */
  C._chordFrame = function(start, end) {
    const dx = end.x - start.x, dy = end.y - start.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1e-9) return null;
    return {
      len: len,
      ux: dx / len, uy: dy / len,       // along the chord
      nx: -dy / len, ny: dx / len,      // left normal
      mx: (start.x + end.x) / 2,        // chord midpoint
      my: (start.y + end.y) / 2
    };
  };

  /**
   * Fit a circular arc through start and end whose apex sits `sag` (signed,
   * feet) off the chord along the left normal.
   * Returns null when the result is effectively straight — callers treat null
   * as "this is a plain wall".
   */
  C.fitArc = function(start, end, sag) {
    const f = C._chordFrame(start, end);
    if (!f) return null;
    if (Math.abs(sag) < C.FLAT_EPS) return null;

    const a = f.len / 2;                       // half chord
    const r = (a * a + sag * sag) / (2 * Math.abs(sag));
    // Centre sits on the far side of the chord from the apex.
    const signed = (sag > 0 ? 1 : -1);
    const off = Math.abs(sag) - r;             // negative when the arc is minor
    const cx = f.mx + f.nx * off * signed;
    const cy = f.my + f.ny * off * signed;

    const a0 = Math.atan2(start.y - cy, start.x - cx);
    const a1 = Math.atan2(end.y - cy, end.x - cx);

    // Pick the sweep direction that actually passes through the apex, so major
    // arcs (|sag| > half-chord) come out right too.
    const apexX = f.mx + f.nx * sag, apexY = f.my + f.ny * sag;
    const apexA = Math.atan2(apexY - cy, apexX - cx);
    const ccw = !window.RoomDesignerMath._angleInArc(apexA, a0, a1, false);

    // `bow` is the sagitta as a fraction of chord length — scale-invariant, so a
    // curve whose endpoints move (room corner drag) can be re-fitted to the new
    // chord without needing a snapshot of the old one. See refitToChord().
    return { cx: cx, cy: cy, r: r, a0: a0, a1: a1, ccw: ccw, sag: sag, bow: sag / f.len };
  };

  /**
   * How far a wall may bow, as a cap on arc length ÷ chord length.
   * Ryan's spec: a 10ft wall may curve IN to at most 13ft of arc, and bulge OUT to
   * at most 16ft. Expressed as a ratio so it scales to any wall length.
   */
  C.MAX_ARC_RATIO_IN = 1.30;
  C.MAX_ARC_RATIO_OUT = 1.60;

  /**
   * arc ÷ chord for a bow `b` = sagitta ÷ HALF-chord.
   *   R = a(1+b²)/2b,  θ = 4·atan(b)  [b is the AutoCAD bulge, tan(θ/4)]
   *   ratio = Rθ/2a = (1+b²)·atan(b)/b
   * Strictly increasing in |b|, which is what makes the inverse below bisectable.
   * Sanity: b=1 is a semicircle and returns π/2.
   */
  C.arcChordRatio = function(b) {
    const ab = Math.abs(b);
    if (ab < 1e-12) return 1;
    return (1 + ab * ab) * Math.atan(ab) / ab;
  };

  /** Largest |sagitta| on `chordLen` whose arc stays within `ratio` × the chord. */
  C.maxSagForRatio = function(chordLen, ratio) {
    if (!(chordLen > 0) || !(ratio > 1)) return 0;
    let lo = 0, hi = 8;                    // b=8 is ratio ~3.4, far past anything usable
    if (C.arcChordRatio(hi) < ratio) return (chordLen / 2) * hi;
    for (let i = 0; i < 60; i++) {
      const mid = (lo + hi) / 2;
      if (C.arcChordRatio(mid) < ratio) lo = mid; else hi = mid;
    }
    return (chordLen / 2) * lo;
  };

  /**
   * Area of the circular segment cut off by a chord of length `chord` with a bow of
   * depth `sag` (the segment between the chord and the arc, not the pie slice).
   *
   *   a = chord/2 ,  θ = 4·atan(sag/a) ,  R = (a² + sag²) / 2·sag
   *   area = (R²/2)·(θ − sin θ)
   *
   * Holds for a major arc too, since θ = 4·atan(b) is the bulge relation rather than
   * an asin that would fold past 180°. Anchor: chord 2 with sag 1 is a semicircle —
   * θ = π, R = 1, area = π/2.
   */
  C.circularSegmentArea = function(chord, sag) {
    const a = Math.abs(chord) / 2;
    const s = Math.abs(sag);
    if (!(a > 1e-12) || s < 1e-12) return 0;
    const theta = 4 * Math.atan(s / a);
    const R = (a * a + s * s) / (2 * s);
    return (R * R / 2) * (theta - Math.sin(theta));
  };

  /**
   * Area of a closed outline whose edges may bow.
   * `verts` is [{x, y, sag}] in order; `sag` bows the edge LEAVING that vertex.
   *
   * Shoelace over the vertices, then each bowed edge's circular segment is added or
   * subtracted. The sign is decided by testing whether the bow's apex lands inside
   * the straight-sided polygon — apex inside means the bow eats area. Deciding it
   * that way rather than from the winding direction avoids reasoning about
   * handedness in a y-down canvas, and makes the result identical whether the
   * outline was traced clockwise or anticlockwise.
   */
  C.polygonAreaWithBows = function(verts) {
    const M = window.RoomDesignerMath;
    if (!M || !verts || verts.length < 3) return 0;
    const poly = verts.map(v => ({ x: v.x, y: v.y }));
    let area = Math.abs(M.signedPolygonArea(poly));
    for (let i = 0; i < verts.length; i++) {
      const p = verts[i], q = verts[(i + 1) % verts.length];
      const sag = p.sag || 0;
      if (Math.abs(sag) < 1e-9) continue;
      const dx = q.x - p.x, dy = q.y - p.y;
      const chord = Math.sqrt(dx * dx + dy * dy);
      // A vanishing chord with a real sag is a degenerate edge — two vertices dropped
      // on top of each other. The segment formula happily returns most of a full
      // circle for it (a 1e-9 chord with a 4ft sag yields ~12.6 sq ft), so throw it
      // out rather than let a stray double-click poison the area.
      if (chord < 1e-6 || chord < Math.abs(sag) * 1e-6) continue;
      const seg = C.circularSegmentArea(chord, sag);
      const nx = -dy / chord, ny = dx / chord;
      const apex = { x: (p.x + q.x) / 2 + nx * sag, y: (p.y + q.y) / 2 + ny * sag };
      area += M.pointInPolygon(apex.x, apex.y, poly) ? -seg : seg;
    }
    return area;
  };

  /** Signed sweep magnitude (radians, always positive) of a fitted arc. */
  C.sweepOf = function(arc) {
    if (!arc) return 0;
    return arc.ccw ? norm(arc.a0 - arc.a1) : norm(arc.a1 - arc.a0);
  };

  /** Arc length in feet. Falls back to chord length when not curved. */
  C.arcLength = function(arc, start, end) {
    if (!arc) {
      if (!start || !end) return 0;
      return Math.hypot(end.x - start.x, end.y - start.y);
    }
    return arc.r * C.sweepOf(arc);
  };

  /**
   * Signed perpendicular offset of `point` from the start→end chord.
   * This is the value the bulge drag handle writes.
   */
  C.sagittaFrom = function(start, end, point) {
    const f = C._chordFrame(start, end);
    if (!f) return 0;
    return (point.x - f.mx) * f.nx + (point.y - f.my) * f.ny;
  };

  /** Where the drag handle sits: the arc apex, or the chord midpoint if straight. */
  C.midHandlePoint = function(start, end, sag) {
    const f = C._chordFrame(start, end);
    if (!f) return { x: start.x, y: start.y };
    const s = sag || 0;
    return { x: f.mx + f.nx * s, y: f.my + f.ny * s };
  };

  // ── Openings ──────────────────────────────
  //
  // A door or window on a curve is represented as a reserved span of the arc.
  // Tessellation turns each span into EXACTLY ONE facet — the chord across that
  // piece of the arc — so the door binds to an ordinary straight wall and the
  // whole door subsystem (door.wall, hingeParam/endParam, and the ~52 sites that
  // derive door points) keeps working untouched.
  //
  // The chord never shows: it lies inside the opening, which _clearDoorGap erases.
  // Both jambs are points ON the arc, so the wall reads as continuous.

  /**
   * An opening is stored by PHYSICAL WIDTH, not by arc-param span:
   *
   *     { tc, w, id }    tc = centre as an arc param 0..1
   *                      w  = chord width in feet (the door's actual width)
   *
   * The arc-param span is derived from `w` every time the curve is laid out. Storing
   * the span instead would make a 3ft door grow to 3.32ft the moment you deepened
   * the bow, because the same angular span on a smaller radius is a longer chord.
   *
   * Returns resolved spans `{t0,t1,tc,w,id}`, or NULL when this arc cannot host them
   * — the width no longer fits the radius, or two openings now collide. Null means
   * the caller must refuse the reshape rather than mangle a door.
   */
  C.resolveOpenings = function(arc, openings) {
    if (!openings || !openings.length) return [];
    if (!arc) return null;
    const out = [];
    for (const o of openings) {
      const dt = C.spanForChord(arc, o.w);
      if (!isFinite(dt) || dt <= 0 || dt >= 1) return null;    // won't fit this radius
      // Keep a minimum jamb at each end. Besides matching how a real opening sits
      // in a wall, it guarantees every free region has non-zero length, which is
      // what lets facetPlan give each region a fixed facet count.
      const lo = dt / 2 + C.MIN_JAMB, hi = 1 - dt / 2 - C.MIN_JAMB;
      if (lo > hi) return null;                                 // no room for jambs
      const tc = Math.max(lo, Math.min(hi, o.tc));
      out.push({ t0: tc - dt / 2, t1: tc + dt / 2, tc: tc, w: o.w, id: o.id });
    }
    out.sort((a, b) => a.t0 - b.t0);
    for (let i = 1; i < out.length; i++) {
      if (out[i].t0 < out[i - 1].t1 - 1e-9) return null;       // collide after resolving
    }
    return out;
  };

  /**
   * Facet layout for a curve: `bounds` are ascending params 0..1, one facet
   * between each consecutive pair, and `opening[k]` marks facet k as an opening.
   * Free regions are subdivided at roughly the density of an unbroken curve.
   * Returns null when the openings cannot be resolved onto this arc.
   */
  C.facetPlan = function(arc, n, openings) {
    const count = Math.max(2, (n | 0) || C.DEFAULT_FACETS);
    const ops = C.resolveOpenings(arc, openings);
    if (ops === null) return null;
    const regions = ops.length + 1;
    // Each free region gets a FIXED number of facets, not a share proportional to
    // its length. That makes the total facet count a function of (n, opening COUNT)
    // alone — independent of how wide the openings are or where they sit.
    //
    // That invariant is what makes both reshaping and sliding safe: neither changes
    // the number of openings, so neither changes the facet count or the position of
    // the opening facets, so applyToFacets can keep mutating in place and every
    // `door.wall` reference stays valid. Only adding or removing an opening changes
    // the layout, and that is a deliberate rebuild-and-rebind.
    //
    // Proportional allocation cannot work here: an opening's angular span depends on
    // the radius, so the free regions' shares would shift on every reshape.
    const perRegion = Math.max(8, Math.ceil(count / regions));
    const bounds = [0];
    const opening = [];
    let cursor = 0;
    const fill = (from, to) => {
      const span = to - from;
      for (let k = 1; k <= perRegion; k++) {
        bounds.push(from + span * (k / perRegion));
        opening.push(false);
      }
    };
    for (const op of ops) {
      fill(cursor, op.t0);                 // free region, ending exactly on t0
      bounds.push(op.t1); opening.push(true);
      cursor = op.t1;
    }
    fill(cursor, 1);
    bounds[bounds.length - 1] = 1;
    return { bounds: bounds, opening: opening, ops: ops };
  };

  /**
   * Inverse of pointAtParam: the arc param 0..1 nearest a world point.
   * Used to turn a click on a curve into a position along it.
   */
  C.paramAtPoint = function(arc, start, end, p) {
    if (!arc) {
      const dx = end.x - start.x, dy = end.y - start.y;
      const lenSq = dx * dx + dy * dy;
      if (lenSq < 1e-12) return 0;
      return Math.max(0, Math.min(1, ((p.x - start.x) * dx + (p.y - start.y) * dy) / lenSq));
    }
    const sweep = C.sweepOf(arc);
    if (sweep <= 1e-9) return 0;
    const ang = Math.atan2(p.y - arc.cy, p.x - arc.cx);
    let d = (arc.ccw ? -1 : 1) * (ang - arc.a0);
    d = ((d % TWO_PI) + TWO_PI) % TWO_PI;
    if (d > sweep) {
      // Behind the start or past the end — snap to the nearer terminus.
      return (d - sweep) < (TWO_PI - d) ? 1 : 0;
    }
    return Math.max(0, Math.min(1, d / sweep));
  };

  /** A single point at arc param t (0..1), with the endpoints snapped exactly. */
  C.pointAtParam = function(arc, start, end, t) {
    if (t <= 0) return { x: start.x, y: start.y };
    if (t >= 1) return { x: end.x, y: end.y };
    if (!arc) return { x: start.x + (end.x - start.x) * t, y: start.y + (end.y - start.y) * t };
    const ang = arc.a0 + (arc.ccw ? -1 : 1) * C.sweepOf(arc) * t;
    return { x: arc.cx + arc.r * Math.cos(ang), y: arc.cy + arc.r * Math.sin(ang) };
  };

  /**
   * The arc-param width of a span whose CHORD is `chordFeet`. A 3ft door leaf is
   * 3ft straight, so an opening reserves the span its chord subtends, not the span
   * whose arc length equals the width.
   * Returns NaN when the chord cannot fit on this circle at all.
   */
  C.spanForChord = function(arc, chordFeet) {
    if (!arc || !(chordFeet > 0)) return NaN;
    const half = chordFeet / (2 * arc.r);
    if (half >= 1) return NaN;             // chord longer than the diameter
    const sweep = C.sweepOf(arc);
    if (sweep <= 1e-9) return NaN;
    return (2 * Math.asin(half)) / sweep;
  };

  /**
   * Position an opening of physical width `w` centred as near `tCenter` as it will
   * go — clamped inside the curve and walked clear of the other openings.
   * Returns `{tc, w, id}` ready to store, or null if it will not fit.
   * Pass an existing `id` when MOVING that opening, so it ignores its own old span.
   */
  C.fitOpening = function(arc, openings, tCenter, w, id) {
    const dt = C.spanForChord(arc, w);
    if (!isFinite(dt) || dt <= 0 || dt >= 1) return null;
    const others = C.resolveOpenings(arc, (openings || []).filter(o => o.id !== id));
    if (others === null) return null;
    // Same jamb-aware bounds resolveOpenings uses, so what we return here survives
    // resolution unmoved rather than being silently nudged later.
    const lo = dt / 2 + C.MIN_JAMB, hi = 1 - dt / 2 - C.MIN_JAMB;
    if (lo > hi) return null;
    let tc = Math.max(lo, Math.min(hi, tCenter));
    const hits = (c) => others.find(o => (c - dt / 2) < o.t1 - 1e-9 && (c + dt / 2) > o.t0 + 1e-9);
    for (let pass = 0; pass <= others.length; pass++) {
      const clash = hits(tc);
      if (!clash) break;
      // Prefer sliding past the far side, fall back to the near side.
      const fwd = clash.t1 + dt / 2, back = clash.t0 - dt / 2;
      if (fwd <= hi + 1e-9) tc = Math.min(hi, fwd);
      else if (back >= lo - 1e-9) tc = Math.max(lo, back);
      else return null;
    }
    if (hits(tc)) return null;
    return { tc: tc, w: w, id: id };
  };

  // ── Tessellation ──────────────────────────

  /**
   * Points along the arc from start to end — one per facet boundary.
   * The two endpoints are snapped to the EXACT start/end values rather than
   * recomputed from the arc — the engine keys wall endpoints to 1/1200 ft to
   * chain walls into paths, and float drift there would break the chain.
   */
  C.tessellate = function(arc, start, end, n, openings) {
    const plan = C.facetPlan(arc, n, openings);
    if (!plan) return null;
    const pts = plan.bounds.map(t => C.pointAtParam(arc, start, end, t));
    pts[0] = { x: start.x, y: start.y };
    pts[pts.length - 1] = { x: end.x, y: end.y };
    return pts;
  };

  // ── Facet lifecycle ───────────────────────

  /** All facets of a curve, in draw order. */
  C.facetsOf = function(room, curveId) {
    if (!room || !room.walls || !curveId) return [];
    return room.walls
      .filter(w => w.curveId === curveId)
      .sort((p, q) => (p.curveSeq || 0) - (q.curveSeq || 0));
  };

  /**
   * First-time creation: turn one straight wall's endpoints into n facet walls.
   * Returns NEW wall objects — the caller splices them in place of the original.
   */
  C.buildFacets = function(start, end, sag, n, openings, reuseCurveId) {
    const count = Math.max(2, (n | 0) || C.DEFAULT_FACETS);
    const arc = C.fitArc(start, end, sag);
    if (!arc) return null;
    arc.n = count;
    arc.openings = (openings || []).map(o => ({ tc: o.tc, w: o.w, id: o.id }));
    const plan = C.facetPlan(arc, count, arc.openings);
    if (!plan) return null;
    // Adding or removing an opening rebuilds the facet list but must NOT mint a new
    // curve — pass the existing id so selection, labels and lookups stay attached.
    const curveId = reuseCurveId || C.nextCurveId();
    const pts = C.tessellate(arc, start, end, count, arc.openings);
    const facets = [];
    for (let k = 0; k < pts.length - 1; k++) {
      facets.push({
        start: { x: pts[k].x, y: pts[k].y },
        end: { x: pts[k + 1].x, y: pts[k + 1].y },
        curveId: curveId,
        curveSeq: k,
        curveArc: arc,
        isOpening: !!plan.opening[k]
      });
    }
    return { curveId: curveId, arc: arc, facets: facets };
  };

  /**
   * Reshape an existing curve IN PLACE. Facet objects and their point objects
   * are mutated, never replaced — that is what keeps `door.wall` references and
   * any other object identity alive across a reshape.
   * `facets` must be the full, seq-ordered set for one curve.
   *
   * Openings default to the ones already on the arc, so a plain reshape keeps its
   * doors. Returns null WITHOUT touching anything when the requested layout needs
   * a different facet count than the array supplied — that is a rebuild, and
   * silently mutating a mismatched array would scramble door bindings.
   */
  C.applyToFacets = function(facets, start, end, sag, openings) {
    if (!facets || facets.length === 0) return null;
    const existing = facets[0].curveArc;
    const ops = (openings !== undefined ? openings : (existing && existing.openings)) || [];
    const count = (existing && existing.n) || facets.length;
    const arcTry = C.fitArc(start, end, sag);
    if (!arcTry) return null;
    const plan = C.facetPlan(arcTry, count, ops);
    if (!plan) return null;      // openings no longer fit this radius — refuse
    // In-place mutation is only safe when the layout is IDENTICAL — same facet
    // count AND the opening facets in the same positions. A matching count alone
    // is not enough: two different opening sets can coincidentally produce the
    // same number of facets, and mutating then silently remaps which facet is the
    // opening, leaving every door bound to the wrong wall. Anything else is a
    // rebuild, which the caller must do explicitly so it can rebind doors.
    if (plan.bounds.length - 1 !== facets.length) return null;
    for (let k = 0; k < facets.length; k++) {
      if (!!plan.opening[k] !== !!facets[k].isOpening) return null;
    }
    const arc = arcTry;
    arc.n = count;
    arc.openings = ops.map(o => ({ tc: o.tc, w: o.w, id: o.id }));
    const pts = C.tessellate(arc, start, end, count, arc.openings);
    for (let k = 0; k < facets.length; k++) {
      const f = facets[k];
      f.start.x = pts[k].x;     f.start.y = pts[k].y;
      f.end.x = pts[k + 1].x;   f.end.y = pts[k + 1].y;
      f.curveSeq = k;
      f.curveArc = arc;
      f.isOpening = !!plan.opening[k];
    }
    return arc;
  };

  /**
   * Move an existing opening to `tc`, CLAMPED to the gap it currently sits in.
   *
   * Distinct from fitOpening, which hops an opening over anything in its way — fine
   * when placing, wrong when dragging, where hopping would teleport the door past
   * its neighbour. Clamping also means an opening can never change places with
   * another, which keeps the openings in a stable order and therefore keeps each
   * opening's facet index fixed. That is what lets a slide stay an in-place
   * mutation and keeps `door.wall` valid.
   */
  C.clampOpeningCentre = function(arc, openings, tc, w, id) {
    const dt = C.spanForChord(arc, w);
    if (!isFinite(dt) || dt <= 0 || dt >= 1) return null;
    const others = C.resolveOpenings(arc, (openings || []).filter(o => o.id !== id));
    if (others === null) return null;
    let lo = dt / 2 + C.MIN_JAMB, hi = 1 - dt / 2 - C.MIN_JAMB;
    const cur = (openings || []).find(o => o.id === id);
    const at = cur ? cur.tc : tc;
    for (const o of others) {
      if (o.t1 <= at - dt / 2 + 1e-9) lo = Math.max(lo, o.t1 + dt / 2 + C.MIN_JAMB);
      else if (o.t0 >= at + dt / 2 - 1e-9) hi = Math.min(hi, o.t0 - dt / 2 - C.MIN_JAMB);
    }
    if (lo > hi) return null;
    return { tc: Math.max(lo, Math.min(hi, tc)), w: w, id: id, atStart: tc <= lo + 1e-9, atEnd: tc >= hi - 1e-9 };
  };

  /** Facet index of the opening carrying `id`, or -1. */
  C.openingFacetIndex = function(arc, id) {
    if (!arc) return -1;
    const plan = C.facetPlan(arc, arc.n || C.DEFAULT_FACETS, arc.openings);
    if (!plan) return -1;
    let seen = 0;
    for (let k = 0; k < plan.opening.length; k++) {
      if (!plan.opening[k]) continue;
      if (plan.ops[seen] && plan.ops[seen].id === id) return k;
      seen++;
    }
    return -1;
  };

  /**
   * Re-fit a curve to its facets' CURRENT termini, preserving the bow ratio.
   * Called after something else has moved a terminus (a room-corner drag, an
   * endpoint drag) — without this the arc would stay pinned to the old chord and
   * the curve would visibly tear away from the corner.
   */
  C.refitToChord = function(facets) {
    if (!facets || facets.length === 0) return null;
    const arc = facets[0].curveArc;
    if (!arc) return null;
    const ends = C.endpointsOf(facets);
    const len = Math.hypot(ends.end.x - ends.start.x, ends.end.y - ends.start.y);
    if (len < 1e-6) return null;
    const bow = (arc.bow != null) ? arc.bow : (arc.sag / len);
    return C.applyToFacets(facets, ends.start, ends.end, bow * len);
  };

  /**
   * The un-curve gesture: collapse a facet group back to a single straight wall.
   * Returns a plain wall object spanning the curve's two termini.
   */
  C.collapseToStraight = function(facets) {
    if (!facets || facets.length === 0) return null;
    const first = facets[0], last = facets[facets.length - 1];
    return {
      start: { x: first.start.x, y: first.start.y },
      end: { x: last.end.x, y: last.end.y }
    };
  };

  /** The chord endpoints of a curve (its two termini). */
  C.endpointsOf = function(facets) {
    if (!facets || facets.length === 0) return null;
    return { start: facets[0].start, end: facets[facets.length - 1].end };
  };

  /**
   * True when `point` is an interior joint between two facets of the same curve
   * (as opposed to one of the curve's two termini). Interior joints must not be
   * individually draggable — dragging one would tear the arc.
   */
  C.isInteriorJoint = function(facets, point, eps) {
    if (!facets || facets.length < 2) return false;
    const e = eps || 1e-4;
    const near = (p, q) => Math.abs(p.x - q.x) < e && Math.abs(p.y - q.y) < e;
    const ends = C.endpointsOf(facets);
    if (near(point, ends.start) || near(point, ends.end)) return false;
    for (let k = 0; k < facets.length; k++) {
      if (near(point, facets[k].start) || near(point, facets[k].end)) return true;
    }
    return false;
  };

  return C;
})();
/* END CUSTOM_ROOMDESIGNER_BY_CLAUDE */
